'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Mic,
  Users,
  Briefcase,
  MessageSquare,
  User as UserIcon,
  Check,
  ChevronRight,
  MapPin,
  Calendar,
  Play,
} from 'lucide-react';
import { Button as DsButton } from '@/components/ds';
import { useAnalytics } from '@/components/providers/AnalyticsProvider';

/* ------------------------------------------------------------------ */
/* Local marketing-only primitives — not promoted to the design system */
/* ------------------------------------------------------------------ */

function Waveform({ count = 48, accent = false }: { count?: number; accent?: boolean }) {
  const bars = Array.from({ length: count }, (_, i) => {
    const h = 6 + Math.abs(Math.sin(i * 0.7) * 28) + Math.abs(Math.cos(i * 0.31) * 18);
    return Math.min(48, h);
  });
  return (
    <div className="flex items-center gap-0.5 h-14" aria-hidden>
      {bars.map((h, i) => {
        const isAccentBar = accent && i / count <= 0.4;
        return (
          <div
            key={i}
            className={`w-[3px] rounded-[2px] ${isAccentBar ? 'bg-accent' : 'bg-surface-3'}`}
            style={{ height: `${h}px` }}
          />
        );
      })}
    </div>
  );
}

function TranscriptCard({
  role,
  time,
  text,
  className = '',
}: {
  role: string;
  time: string;
  text: string;
  className?: string;
}) {
  return (
    <div
      className={`absolute w-60 rounded-[10px] bg-surface-2 border border-border-subtle p-3 shadow-[0_8px_24px_rgba(0,0,0,0.3)] ${className}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Mic className="w-3 h-3 text-accent" strokeWidth={1.75} />
        <span className="text-mono font-mono uppercase text-text-tertiary">{role}</span>
        <span className="text-mono font-mono uppercase text-text-tertiary ml-auto">
          {time}
        </span>
      </div>
      <p className="italic text-[13px] leading-[1.4] text-text-primary">&ldquo;{text}&rdquo;</p>
    </div>
  );
}

const ACCENT_GLOW =
  'shadow-[0_0_0_1px_oklch(0.62_0.22_270/0.5),0_8px_24px_oklch(0.62_0.22_270/0.25)]';
const ACCENT_GLOW_LG =
  'shadow-[0_0_0_1px_oklch(0.62_0.22_270/0.5),0_12px_32px_oklch(0.62_0.22_270/0.3)]';
const ITALIC_GRADIENT =
  'bg-[linear-gradient(180deg,oklch(0.78_0.18_270)_0%,oklch(0.55_0.24_270)_100%)] bg-clip-text text-transparent';

const NAV_LINKS: { label: string; href: string }[] = [
  { label: 'For talent', href: '#what-you-get' },
  { label: 'For casting', href: '#what-you-get' },
  { label: 'How it works', href: '#how-it-works' },
  { label: 'About', href: '#craft' },
];

const HERO_STATS = [
  { headline: 'Free', body: 'Always, for talent' },
  { headline: 'Direct', body: 'Message casting directly' },
  { headline: 'Built by VOs', body: 'Designed by working actors' },
];

const FEATURES = [
  {
    n: '01',
    icon: Users,
    title: 'Connect with the community',
    body: 'Join groups, message peers, and share your latest work in a feed built for voice actors.',
  },
  {
    n: '02',
    icon: Briefcase,
    title: 'Find your next role',
    body: 'Browse casting calls, submit auditions, and get hired by directors looking for voices like yours.',
  },
  {
    n: '03',
    icon: Mic,
    title: 'Showcase your craft',
    body: 'Build a portfolio with demo reels, photos, and a résumé that casting directors actually read.',
  },
];

type StepDetail = { label: string; filename: string; time: string };
const STEPS: {
  n: string;
  title: string;
  body: string;
  icon: typeof UserIcon;
  detail: StepDetail;
}[] = [
  {
    n: 'Step 01',
    title: 'Build your profile',
    body: 'Upload demo reels, set your specialties, and write your story. Your booth, your voice, your terms.',
    icon: UserIcon,
    detail: { label: 'DEMO REEL', filename: 'fantasy_narration.mp3', time: '2:14' },
  },
  {
    n: 'Step 02',
    title: 'Browse the work',
    body: 'Audiobook, animation, commercial, indie game. Filter by role type, rate, deadline. Apply with one click.',
    icon: Briefcase,
    detail: {
      label: 'OPEN ROLES',
      filename: 'Lead Voice — Animated Series',
      time: '2 days ago',
    },
  },
  {
    n: 'Step 03',
    title: 'Get heard, get booked',
    body: 'Casting directors message you directly. No middlemen. No agents skimming your rate. Just the work.',
    icon: MessageSquare,
    detail: { label: 'INBOX', filename: 'Indie Animation Studio', time: '10 min ago' },
  },
];

const CRAFT_BULLETS = [
  'Free for talent — pay nothing to join',
  'No engagement-bait algorithms — chronological feeds',
  'Direct messaging with casting directors',
  'Profiles that show your range — audio reels, video, photos',
];

const FOOTER_LINKS = [
  { label: 'Privacy', href: '/privacy-policy' },
  { label: 'Terms', href: '/terms-of-service' },
  { label: 'Help', href: '/help' },
  { label: 'Contact', href: '/contact' },
];

export default function ExplorePage() {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent('page_viewed', {
      page: 'explore',
      title: 'Indiestry — Explore',
    });
  }, [trackEvent]);

  const trackCTA = (location: string) => {
    trackEvent('cta_clicked', { page: 'explore', location });
  };

  return (
    <div className="min-h-screen bg-surface-0 text-text-primary">
      {/* ─── Top bar ─── */}
      <header className="sticky top-0 z-10 h-16 px-4 sm:px-6 lg:px-8 flex items-center bg-surface-0/70 backdrop-blur-sm border-b border-border-subtle">
        <Link href="/" className="flex items-center gap-2.5 hover:opacity-90 transition-opacity">
          <Image
            src="/indiestrylogofull.png"
            alt="Indiestry"
            width={32}
            height={32}
            className="rounded-full"
            priority
          />
          <span className="text-base font-semibold tracking-tight text-text-primary">
            Indiestry
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 ml-12">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link
            href="/login"
            onClick={() => trackCTA('topbar_login')}
            className="hidden sm:inline-flex h-8 items-center px-3 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Log in
          </Link>
          <Link href="/signup" onClick={() => trackCTA('topbar_signup')}>
            <DsButton variant="primary" size="sm">
              Sign up
            </DsButton>
          </Link>
        </div>
      </header>

      {/* ─── Hero ─── */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-16 md:py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_800px_400px_at_50%_60%,oklch(0.62_0.22_270/0.18),transparent_70%)]"
        />
        <div className="relative max-w-[1100px] mx-auto">
          <div className="flex flex-col items-center text-center gap-2">
            <span className="text-mono font-mono uppercase text-accent">For voice actors</span>
            <h1 className="m-0 max-w-[880px] font-semibold leading-[0.95] tracking-[-0.025em] text-5xl md:text-[80px]">
              Where artists find their{' '}
              <span
                className={`italic font-bold inline-block translate-y-1 text-6xl md:text-[96px] leading-[0.95] ${ITALIC_GRADIENT}`}
              >
                voice.
              </span>
            </h1>

            <div className="mt-7 mb-3">
              <Waveform count={42} accent />
            </div>

            <p className="max-w-[540px] text-text-secondary text-base leading-relaxed">
              Connect with casting directors, build your portfolio, and land your next role.
              Built by voice actors, for voice actors.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <Link
                href="/signup"
                onClick={() => trackCTA('hero_primary')}
              >
                <DsButton
                  variant="primary"
                  size="md"
                  className={`!h-12 !px-6 !text-sm ${ACCENT_GLOW}`}
                >
                  Join the community
                  <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                </DsButton>
              </Link>
              <Link
                href="/login"
                onClick={() => trackCTA('hero_secondary')}
                className="inline-flex h-12 items-center px-4 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                Sign in
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-[720px] mt-12">
              {HERO_STATS.map((s) => (
                <div
                  key={s.headline}
                  className="text-left bg-surface-2 border border-border-subtle rounded-md px-[18px] py-4"
                >
                  <div className="text-lg font-semibold text-accent tracking-[-0.01em]">
                    {s.headline}
                  </div>
                  <div className="text-xs text-text-secondary mt-1">{s.body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Floating transcript cards (decorative, desktop only) */}
          <div className="hidden md:block">
            <TranscriptCard
              role="AUDIOBOOK · CH 4"
              time="0:42"
              text="Across the harbor, the lamps were lit one by one — and the city held its breath."
              className="-left-4 top-[120px]"
            />
            <TranscriptCard
              role="ANIMATION · DEMO"
              time="1:18"
              text="You think you're tough? I eat tough for breakfast. With cereal."
              className="-right-4 top-[200px]"
            />
          </div>
        </div>
      </section>

      {/* ─── What you get ─── */}
      <section
        id="what-you-get"
        className="px-4 sm:px-6 lg:px-8 py-16 md:py-20 border-t border-border-subtle bg-surface-1"
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14">
            <span className="text-mono font-mono uppercase text-accent">What you get</span>
            <h2 className="mt-2 mb-1.5 text-3xl md:text-[40px] leading-[1.1] font-semibold tracking-[-0.02em]">
              Everything you need to build your career
            </h2>
            <p className="text-text-secondary text-base">
              From your first audition to your{' '}
              <em className="not-italic font-medium text-text-primary">hundredth</em> role.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FEATURES.map((c) => {
              const FeatureIcon = c.icon;
              return (
                <div
                  key={c.n}
                  className="relative overflow-hidden p-6 bg-surface-2 border border-border-subtle rounded-md"
                >
                  <div className="flex items-start mb-5">
                    <div className="w-11 h-11 rounded-[10px] bg-accent-soft text-accent grid place-items-center">
                      <FeatureIcon className="w-[22px] h-[22px]" strokeWidth={1.75} />
                    </div>
                    <span className="ml-auto text-mono font-mono uppercase text-text-tertiary">
                      {c.n} / 03
                    </span>
                  </div>
                  <h3 className="m-0 mb-2 text-lg font-semibold tracking-[-0.005em]">
                    {c.title}
                  </h3>
                  <p className="m-0 text-text-secondary text-sm leading-[1.55]">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section
        id="how-it-works"
        className="px-4 sm:px-6 lg:px-8 py-16 md:py-20 border-t border-border-subtle"
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14">
            <span className="text-mono font-mono uppercase text-accent">How it works</span>
            <h2 className="mt-2 mb-1.5 text-3xl md:text-[40px] leading-[1.1] font-semibold tracking-[-0.02em]">
              Three steps to your next role
            </h2>
            <p className="text-text-secondary text-base">
              No gatekeepers. No agents. No bidding wars.
            </p>
          </div>

          <div className="relative">
            {/* connecting line, desktop only */}
            <div
              aria-hidden
              className="hidden md:block absolute left-8 top-8 bottom-8 w-0.5 bg-border-subtle"
            />
            <div className="flex flex-col gap-6">
              {STEPS.map((s, i) => {
                const StepIcon = s.icon;
                return (
                  <div key={s.n} className="flex flex-col md:flex-row gap-4 md:gap-6 md:items-start">
                    <div className="w-16 h-16 rounded-full bg-surface-2 border-2 border-accent grid place-items-center flex-shrink-0 relative z-[1]">
                      <StepIcon className="w-6 h-6 text-accent" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4 md:gap-6 p-6 bg-surface-2 border border-border-subtle rounded-md md:items-center">
                      <div>
                        <span className="text-mono font-mono uppercase text-text-tertiary">
                          {s.n}
                        </span>
                        <h3 className="my-1.5 text-2xl font-semibold tracking-[-0.01em]">
                          {s.title}
                        </h3>
                        <p className="m-0 max-w-[460px] text-text-secondary text-sm leading-[1.6]">
                          {s.body}
                        </p>
                      </div>
                      <div className="bg-surface-1 border border-border-subtle rounded-sm p-3.5">
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className="text-mono font-mono uppercase text-text-tertiary">
                            {s.detail.label}
                          </span>
                          <span className="text-mono font-mono uppercase text-text-tertiary ml-auto">
                            {s.detail.time}
                          </span>
                        </div>
                        <div className="flex items-center gap-2.5 mb-2.5">
                          <div className="w-7 h-7 rounded-full bg-accent text-text-on-accent grid place-items-center flex-shrink-0">
                            {i === 0 && (
                              <Play
                                className="w-3 h-3"
                                fill="currentColor"
                                strokeWidth={0}
                              />
                            )}
                            {i === 1 && (
                              <Briefcase className="w-3 h-3" strokeWidth={2} />
                            )}
                            {i === 2 && (
                              <MessageSquare className="w-3 h-3" strokeWidth={2} />
                            )}
                          </div>
                          <div className="text-[13px] font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                            {s.detail.filename}
                          </div>
                        </div>
                        {i === 0 && <Waveform count={28} accent />}
                        {i === 1 && (
                          <div className="flex gap-1">
                            {['animation', 'voice-acting', 'lead-role'].map((t) => (
                              <span
                                key={t}
                                className="px-2 py-0.5 rounded-full bg-accent-soft text-accent text-[10px] font-medium"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {i === 2 && (
                          <p className="m-0 italic text-[12px] leading-[1.4] text-text-secondary">
                            &ldquo;Loved your fantasy reel. Available for a quick chat this
                            week?&rdquo;
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Respects your craft ─── */}
      <section
        id="craft"
        className="px-4 sm:px-6 lg:px-8 py-16 md:py-20 border-t border-border-subtle bg-surface-1"
      >
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <span className="text-mono font-mono uppercase text-accent">Built for the work</span>
            <h2 className="mt-2 mb-4 text-3xl md:text-[40px] leading-[1.1] font-semibold tracking-[-0.02em]">
              A platform that respects your craft
            </h2>
            <p className="m-0 mb-6 max-w-[480px] text-text-secondary text-[15px] leading-[1.6]">
              Every feature is designed by working voice actors. No algorithms playing games
              with your visibility. No paywalls hiding the basics. Just a clean place to do
              your work and be found.
            </p>
            <ul className="list-none p-0 m-0 mb-8 flex flex-col gap-3">
              {CRAFT_BULLETS.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-text-primary text-sm"
                >
                  <span className="w-[18px] h-[18px] mt-[1px] rounded bg-accent-soft text-accent grid place-items-center flex-shrink-0">
                    <Check className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link href="/signup" onClick={() => trackCTA('craft_cta')}>
              <DsButton variant="primary" size="md">
                Get started
                <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
              </DsButton>
            </Link>
          </div>

          {/* Job card mock */}
          <div className="bg-surface-2 border border-border-subtle rounded-md overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.3)]">
            <div className="flex items-center gap-2 px-3.5 py-2.5 bg-surface-3 border-b border-border-subtle">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" aria-hidden />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" aria-hidden />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" aria-hidden />
              <div className="ml-3 flex-1 px-2.5 py-1 rounded-sm bg-surface-1 text-mono font-mono text-text-tertiary">
                indiestry.com/jobs
              </div>
            </div>
            <div className="p-5">
              <div className="text-xl font-semibold tracking-[-0.01em] mb-1">
                Lead Voice <span className="text-text-tertiary">—</span> Animated Series
              </div>
              <div className="flex items-center gap-1.5 text-text-secondary text-[13px] mb-3.5">
                <span>Indie Animation Studio</span>
                <Check
                  className="w-3 h-3 text-accent"
                  strokeWidth={2.5}
                  aria-label="Verified"
                />
              </div>
              <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-text-tertiary text-xs mb-3.5">
                <span className="inline-flex items-center gap-1">
                  <Briefcase className="w-3 h-3" strokeWidth={1.75} /> Animated Series
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" strokeWidth={1.75} /> Remote
                </span>
                <span className="inline-flex items-center gap-1">
                  <span aria-hidden>$</span> $500–$1000 / session
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" strokeWidth={1.75} /> Posted 2 days ago
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {['animation', 'voice-acting', 'lead-role'].map((t) => (
                  <span
                    key={t}
                    className="px-2 py-1 rounded-full bg-accent-soft text-accent text-[11px] font-medium"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <p className="m-0 mb-4 text-text-secondary text-[13px] leading-[1.5]">
                Seeking a versatile voice actor for a lead role in an upcoming animated
                series. Character is a witty, sarcastic protagonist with strong emotional
                range.
              </p>
              <span
                aria-hidden
                className="inline-flex items-center gap-2 h-8 px-4 rounded-sm bg-accent text-text-on-accent text-[13px] font-semibold select-none"
              >
                Apply Now
                <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section
        id="cta"
        className="relative overflow-hidden px-4 sm:px-6 lg:px-8 py-24 md:py-32 border-t border-border-subtle"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_700px_360px_at_50%_50%,oklch(0.62_0.22_270/0.22),transparent_70%)]"
        />
        <div
          aria-hidden
          className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 opacity-[0.18]"
        >
          <Waveform count={28} />
        </div>
        <div
          aria-hidden
          className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.18] scale-x-[-1]"
        >
          <Waveform count={28} />
        </div>
        <div className="relative max-w-[720px] mx-auto text-center">
          <span className="text-mono font-mono uppercase text-accent">Ready when you are</span>
          <h2 className="my-3 text-4xl md:text-[56px] leading-[1.05] font-semibold tracking-[-0.025em]">
            Your next role is{' '}
            <span
              className={`italic font-bold text-5xl md:text-[64px] leading-[1.05] ${ITALIC_GRADIENT}`}
            >
              listening.
            </span>
          </h2>
          <p className="mt-2 mb-8 text-text-secondary text-base leading-[1.6]">
            Join thousands of voice actors building their careers on Indiestry.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link href="/signup" onClick={() => trackCTA('final_primary')}>
              <DsButton
                variant="primary"
                size="md"
                className={`!h-14 !px-7 !text-[15px] ${ACCENT_GLOW_LG}`}
              >
                Create your free account
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </DsButton>
            </Link>
            <Link
              href="/login"
              onClick={() => trackCTA('final_signin')}
              className="inline-flex h-14 items-center px-4 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              Already a member? Sign in
            </Link>
          </div>
          <p className="mt-6 text-text-tertiary text-xs">
            No credit card. No agent fees. No catch.
          </p>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="px-4 sm:px-6 lg:px-8 py-8 border-t border-border-subtle bg-surface-1">
        <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-text-tertiary text-xs">
          <div className="flex items-center gap-2">
            <Image
              src="/indiestrylogofull.png"
              alt="Indiestry"
              width={22}
              height={22}
              className="rounded-full"
            />
            <span>© 2026 Indiestry. Built by voice actors.</span>
          </div>
          <div className="flex flex-wrap gap-5">
            {FOOTER_LINKS.map((l) => (
              <Link
                key={l.label}
                href={l.href}
                className="hover:text-text-primary transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
